import React, { useRef, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getDocs, query, collection, where, doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import styled from 'styled-components';
import { MdPersonAdd } from 'react-icons/md';
import { useAuth } from './contexts/AuthContext';

// Role hierarchy
const roleHierarchy = {
    'Majitel': 1,
    'Admin': 2,
    'Moderátor': 3,
    'VIP': 4,
    'User': 5
};

const PridatRoli = ({ closeModal }) => {
    const { user } = useAuth(); // Ověřte, že 'user' je správně inicializován
    const inputRef = useRef(null);
    const [serverError, setServerError] = useState('');
    const [idValid, setIdValid] = useState(false);
    const [idTouched, setIdTouched] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [selectedRole, setSelectedRole] = useState(''); // Track selected role
    const [successMessage, setSuccessMessage] = useState(''); // New state for success message
    const [showSuccessModal, setShowSuccessModal] = useState(false); // New state to control success modal visibility

    // VIP specific state
    const [vipDays, setVipDays] = useState(30);
    const [vipPermanent, setVipPermanent] = useState(false);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus(); // Auto-focus when component mounts
        }

        // Načtení role aktuálně přihlášeného uživatele
        const fetchUserRole = async () => {
            try {
                if (!user?.uid) return;
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    setUserRole(userDocSnap.data().role);
                } else {
                    setServerError('Uživatel nebyl nalezen.');
                }
            } catch (error) {
                console.error('Chyba při načítání role uživatele:', error);
                setServerError('Došlo k chybě při načítání role uživatele.');
            }
        };

        fetchUserRole();
    }, [user?.uid]);

    const formik = useFormik({
        initialValues: {
            id: ''
        },
        validationSchema: Yup.object({
            id: Yup.string().required('ID je povinné').length(5, 'ID musí mít přesně 5 číslic')
        }),
        onSubmit: async () => {
            if (!idValid || !selectedRole) {
                return;
            }

            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('ID', '==', formik.values.id));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setServerError('Uživatel s tímto ID nebyl nalezen.');
                    return;
                }

                const userDocs = querySnapshot.docs;
                for (const userDoc of userDocs) {
                    const userUID = userDoc.id;
                    const userRef = doc(db, 'users', userUID);
                    const targetUserData = (await getDoc(userRef)).data();
                    const targetUserRole = targetUserData.role;

                    // Prověřte, zda má aktuálně přihlášený uživatel dostatečná práva a zda nemění svou vlastní roli
                    if (userUID === user.uid) {
                        setServerError('Nemůžete měnit svou vlastní roli.');
                        return;
                    }

                    if (roleHierarchy[userRole] >= roleHierarchy[targetUserRole]) {
                        setServerError('Nemáte oprávnění měnit roli uživatele se stejnou nebo vyšší rolí.');
                        return;
                    }

                    // Prepare update payload
                    const updatePayload = { role: selectedRole };

                    if (selectedRole === 'VIP') {
                        // validate vipDays
                        if (!vipPermanent) {
                            const d = Number(vipDays);
                            if (!Number.isInteger(d) || d < 1 || d > 365) {
                                setServerError('Dny musí být celé číslo mezi 1 a 365 nebo zvolte Permanentní.');
                                return;
                            }
                            const expiresAt = Timestamp.fromDate(new Date(Date.now() + d * 24 * 60 * 60 * 1000));
                            updatePayload.vip = { durationDays: d, permanent: false, expiresAt };
                        } else {
                            updatePayload.vip = { durationDays: null, permanent: true, expiresAt: null };
                        }
                    } else {
                        // remove vip field if present
                        updatePayload.vip = null;
                    }

                    // apply update
                    await updateDoc(userRef, updatePayload);

                    // Nastavit úspěšnou zprávu a zobrazit modal
                    setSuccessMessage(`Role ${selectedRole} úspěšně přidána pro ID ${formik.values.id}`);
                    setShowSuccessModal(true);
                }
            } catch (error) {
                console.error('Chyba při aktualizaci role:', error);
                setServerError('Došlo k chybě při aktualizaci role.');
            }
        }
    });

    const checkId = async () => {
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('ID', '==', formik.values.id));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setIdValid(true);
                setServerError(''); // Clear previous error message
            } else {
                setIdValid(false);
                setServerError('Toto ID není zaregistrováno v databázi.');
            }
        } catch (error) {
            console.error('Chyba při ověřování ID:', error);
            setIdValid(false);
            setServerError(`Došlo k chybě při ověřování ID: ${error.message}`);
        }
    };

    useEffect(() => {
        if (idTouched && formik.values.id.length === 5) {
            checkId();
        }
    }, [idTouched, formik.values.id]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setIdTouched(true);
        }
    };

    const handleRoleSelection = (role) => {
        setSelectedRole(role);
        // reset VIP specifics when switching away
        if (role !== 'VIP') {
            setVipDays(30);
            setVipPermanent(false);
        }
    };

    // Function to handle focus and make sure the field is usable
    const handleFocus = () => {
        if (!formik.values.id) {
            setIdTouched(true); // Trigger validation if the field is focused
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        if (/^\d{0,5}$/.test(value)) { // Allow only numbers and up to 5 digits
            formik.setFieldValue('id', value);
            if (value.length === 5) {
                setIdTouched(true);
            } else {
                setIdTouched(false);
                setIdValid(false);
            }
        }
    };

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
        closeModal(); // Close the main modal after the success popup is closed
    };

    return (
        <>
            <ModalContent>
                        <ModalTitle><MdPersonAdd size={20} style={{ color:'#cde' }} />PŘIDAT ROLI</ModalTitle>
                <form onSubmit={formik.handleSubmit}>
                    <ModalInput 
                        type="text"
                        value={formik.values.id}
                        placeholder="Zadejte ID uživatele"
                        autoComplete="off"
                        autoCorrect="off"
                        ref={inputRef}
                        onChange={handleInputChange} // Handle input change
                        onBlur={() => setIdTouched(true)}
                        onFocus={handleFocus} // Add onFocus handler
                        onKeyDown={handleKeyPress}
                        style={{ borderColor: idValid ? '#2ecc71' : '#ccc' }}
                    />
                    {formik.touched.id && formik.errors.id ? (
                        <ErrorText>{formik.errors.id}</ErrorText>
                    ) : idValid ? (
                        <SuccessText>ID je zaregistrováno.</SuccessText>
                    ) : null}
                    {serverError && !idValid && (
                        <ErrorText>{serverError}</ErrorText>
                    )}
                    <RoleButtons>
                        <RoleButton
                            type="button"
                            onClick={() => handleRoleSelection('VIP')}
                            active={selectedRole === 'VIP'}
                            disabled={!idValid}
                        >
                            VIP
                        </RoleButton>
                        <RoleButton
                            type="button"
                            onClick={() => handleRoleSelection('User')}
                            active={selectedRole === 'User'}
                            disabled={!idValid}
                        >
                            Uživatel
                        </RoleButton>
                        <RoleButton
                            type="button"
                            onClick={() => handleRoleSelection('Moderátor')}
                            active={selectedRole === 'Moderátor'}
                            disabled={!idValid}
                        >
                            Moderátor
                        </RoleButton>
                    </RoleButtons>

                    {/* VIP options */}
                    {selectedRole === 'VIP' && (
                        <>
                            <VipRow>
                                <label style={{display:'flex', alignItems:'center', gap:8}}>
                                    <input
                                        type="checkbox"
                                        checked={vipPermanent}
                                        onChange={(e) => setVipPermanent(e.target.checked)}
                                        disabled={!idValid}
                                    />
                                    Permanentní
                                </label>

                                {!vipPermanent && (
                                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                                      <button
                                        type="button"
                                        onMouseDown={(e)=>e.stopPropagation()}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setVipDays(d => {
                                            const cur = Number(d) || 0;
                                            const next = Math.max(1, Math.min(365, cur - 1));
                                            return next;
                                          });
                                        }}
                                        disabled={!idValid}
                                        aria-label="Snížit dny"
                                        style={{padding:'8px 10px',borderRadius:6,cursor:'pointer'}}
                                      >−</button>

                                      <VipDaysInput
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={vipDays}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === '') {
                                            setVipDays('');
                                            return;
                                          }
                                          const n = Number(v);
                                          if (!Number.isNaN(n)) {
                                            const clamped = Math.max(1, Math.min(365, Math.trunc(n)));
                                            setVipDays(clamped);
                                          }
                                        }}
                                        onMouseDown={(e) => { e.stopPropagation(); }}
                                        onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                                        onFocus={(e) => { e.currentTarget.select(); }}
                                        disabled={!idValid}
                                        aria-label="Počet dní VIP"
                                        style={{width: '100px', textAlign:'center'}}
                                      />

                                      <button
                                        type="button"
                                        onMouseDown={(e)=>e.stopPropagation()}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setVipDays(d => {
                                            const cur = Number(d) || 0;
                                            const next = Math.max(1, Math.min(365, cur + 1));
                                            return next;
                                          });
                                        }}
                                        disabled={!idValid}
                                        aria-label="Zvýšit dny"
                                        style={{padding:'8px 10px',borderRadius:6,cursor:'pointer'}}
                                      >+</button>
                                    </div>
                                )}
                            </VipRow>
                            <NoteSmall>Maximálně 365 dní; nebo zvol Permanentní.</NoteSmall>
                        </>
                    )}

                    {serverError && idValid && (
                        <ErrorText>{serverError}</ErrorText>
                    )}
                    <ModalButtons>
                        <ModalButton type="submit" disabled={!idValid || !selectedRole}>Uložit</ModalButton>
                        <ModalButton type="button" onClick={closeModal}>Zrušit</ModalButton>
                    </ModalButtons>
                </form>
            </ModalContent>

            {showSuccessModal && (
                <SuccessModal>
                    <SuccessMessage>{successMessage}</SuccessMessage>
                    <SuccessButton onClick={handleSuccessModalClose}>Zavřít</SuccessButton>
                </SuccessModal>
            )}
        </>
    );
};

// Stylované komponenty
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 520px;
    max-width: calc(100vw - 48px);
    padding: 20px 22px;
    border-radius: 12px;
    box-sizing: border-box;
    background: #071014;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.04);
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
`;

const ModalTitle = styled.h2`
    margin: 6px 0 18px 0;
    font-size: 20px;
    text-align: left;
    display:flex; align-items:center; gap:10px; font-weight:700;
`;

const ModalInput = styled.input`
    margin-bottom: 8px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.08);
    width: 100%;
    font-size: 16px;
    box-sizing: border-box;
    background: rgba(255,255,255,0.02);
    color: #fff;
    &:focus { border-color: #2ecc71; box-shadow: 0 6px 18px rgba(46,204,113,0.06); outline:none; }
`;

const RoleButtons = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    gap: 10px;
    margin: 10px 0 6px 0;
`;

const RoleButton = styled.button`
    background: ${props => props.active ? '#2ecc71' : 'transparent'};
    border: ${props => props.active ? 'none' : '1px solid rgba(255,255,255,0.08)'};
    color: ${props => props.active ? '#fff' : '#cfe6ff'};
    padding: 8px 14px;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 700;
    transition: background .12s ease, transform .08s ease;
    box-shadow: ${props => props.active ? '0 8px 20px rgba(34,197,94,0.12)' : 'none'};
    &:hover { transform: translateY(-2px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform:none; }
`;

const VipRow = styled.div`
    display:flex;
    gap:12px;
    align-items:center;
    margin-top:10px;
    width:100%;
`;

const VipDaysInput = styled.input`
    width:120px;
    padding:8px;
    border-radius:6px;
    border:1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.02);
    color: #fff;
`;

const ErrorText = styled.p`
    color: #e74c3c;
    margin: 4px 0;
    font-size: 14px;
    text-align: center;
`;

const SuccessText = styled.p`
    color: #2ecc71;
    margin: 4px 0;
    font-size: 14px;
    text-align: center;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 18px;
`;

const ModalButton = styled.button`
    background: ${props => props.variant === 'secondary' ? '#2b2f33' : '#22c55e'};
    border: none;
    color: #fff;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    transition: transform .12s ease, filter .12s ease;
    &:hover { filter: brightness(1.06); transform: translateY(-2px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

const NoteSmall = styled.div`
    font-size:12px;
    color:#9fb0d6;
    margin-top:6px;
`;

const SuccessModal = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #071014;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    padding: 20px;
    width: 360px;
    text-align: center;
    z-index: 2147483001;
    border: 1px solid rgba(255,255,255,0.04);
`;

const SuccessMessage = styled.p`
    margin-bottom: 20px;
    font-size: 16px;
    color: #2ecc71;
`;

const SuccessButton = styled.button`
    background: #22c55e;
    border: none;
    color: #fff;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    transition: background .12s ease, transform .08s ease;
    &:hover { transform: translateY(-2px); }
`;

export default PridatRoli;
